/**
 * RaDashboard (C-1d) — Regulatory Affairs dashboard, the RA-PLM capstone.
 * Centralised KPI view across the Master Product Register, Registration Tracker,
 * and Variation Management (RA.docx). Composes the three existing stats
 * endpoints — no new backend.
 * Route: /regulatory/dashboard
 */
import { useNavigate } from '@tanstack/react-router';
import { PageHero } from '@edms/design-system';
import styles from './Regulatory.module.css';
import {
  useProductsQuery, useRegistrationsQuery, useVariationsQuery,
  STAGE_LABEL, VARIATION_STATUS_LABEL,
} from './useRegulatory';

export function RaDashboard() {
  const navigate = useNavigate();
  const products = useProductsQuery({});
  const registrations = useRegistrationsQuery({});
  const variations = useVariationsQuery({});

  const p = products.data?.stats;
  const rItems = registrations.data?.items ?? [];
  const vItems = variations.data?.items ?? [];
  const rStats = registrations.data?.stats;
  const vStats = variations.data?.stats;

  // Registrations by stage / variations by status (for the breakdown lists).
  const byStage = rItems.reduce<Record<string, number>>((a, r) => { a[r.stage] = (a[r.stage] ?? 0) + 1; return a; }, {});
  const byVarStatus = vItems.reduce<Record<string, number>>((a, v) => { a[v.status] = (a[v.status] ?? 0) + 1; return a; }, {});

  const loading = products.isLoading || registrations.isLoading || variations.isLoading;

  return (
    <main className={styles.page}>
      <PageHero
        eyebrow="Regulatory Affairs · RA-PLM"
        title="Regulatory Affairs Dashboard"
        subtitle="Centralised management of registrations, renewals, variations, and regulatory lifecycle activities."
      />

      {loading ? (
        <div className={styles.empty}>Loading dashboard…</div>
      ) : (
        <>
          <div className={styles.kpiRow}>
            <button type="button" className={styles.kpiLink} onClick={() => navigate({ to: '/regulatory/products' })}>
              <span className={styles.kpiNum}>{p?.registered ?? 0}</span><span className={styles.kpiLabel}>Total registered products</span>
            </button>
            <button type="button" className={styles.kpiLink} onClick={() => navigate({ to: '/regulatory/products' })}>
              <span className={styles.kpiNum}>{p?.under_registration ?? 0}</span><span className={styles.kpiLabel}>Under registration</span>
            </button>
            <button type="button" className={`${styles.kpiLink} ${(p?.renewals_due_12m ?? 0) > 0 ? styles.kpiWarn : ''}`} onClick={() => navigate({ to: '/regulatory/products' })}>
              <span className={styles.kpiNum}>{p?.renewals_due_12m ?? 0}</span><span className={styles.kpiLabel}>Renewals due (12 mo)</span>
            </button>
            <button type="button" className={styles.kpiLink} onClick={() => navigate({ to: '/regulatory/variations' })}>
              <span className={styles.kpiNum}>{vStats?.in_progress ?? 0}</span><span className={styles.kpiLabel}>Variations in progress</span>
            </button>
            <button type="button" className={`${styles.kpiLink} ${(p?.expired ?? 0) > 0 ? styles.kpiAlert : ''}`} onClick={() => navigate({ to: '/regulatory/products' })}>
              <span className={styles.kpiNum}>{p?.expired ?? 0}</span><span className={styles.kpiLabel}>Expired registrations</span>
            </button>
            <button type="button" className={styles.kpiLink} onClick={() => navigate({ to: '/regulatory/registrations' })}>
              <span className={styles.kpiNum}>{rStats?.in_progress ?? 0}</span><span className={styles.kpiLabel}>Registrations in progress</span>
            </button>
          </div>

          <div className={styles.dashGrid}>
            <section className={styles.panel}>
              <header className={styles.head}>
                <h2 className={styles.panelTitle}>Registrations by stage</h2>
                <button type="button" className={styles.action} onClick={() => navigate({ to: '/regulatory/registrations' })}>Open tracker</button>
              </header>
              {Object.keys(byStage).length === 0 ? <div className={styles.empty}>No active registrations.</div> : (
                <ul className={styles.breakdown}>
                  {Object.keys(STAGE_LABEL).filter((s) => byStage[s]).map((s) => (
                    <li key={s} className={styles.breakRow}>
                      <span className={styles.breakLabel}>{STAGE_LABEL[s]}</span>
                      <span className={styles.breakBarWrap}><span className={styles.breakBar} style={{ width: `${Math.min(100, (byStage[s] ?? 0) * 30)}%` }} /></span>
                      <span className={styles.breakNum}>{byStage[s]}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className={styles.panel}>
              <header className={styles.head}>
                <h2 className={styles.panelTitle}>Variations by status</h2>
                <button type="button" className={styles.action} onClick={() => navigate({ to: '/regulatory/variations' })}>Open variations</button>
              </header>
              {Object.keys(byVarStatus).length === 0 ? <div className={styles.empty}>No variations.</div> : (
                <ul className={styles.breakdown}>
                  {Object.keys(VARIATION_STATUS_LABEL).filter((s) => byVarStatus[s]).map((s) => (
                    <li key={s} className={styles.breakRow}>
                      <span className={styles.breakLabel}>{VARIATION_STATUS_LABEL[s]}</span>
                      <span className={styles.breakBarWrap}><span className={styles.breakBar} style={{ width: `${Math.min(100, (byVarStatus[s] ?? 0) * 30)}%` }} /></span>
                      <span className={styles.breakNum}>{byVarStatus[s]}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className={styles.panel}>
              <header className={styles.head}>
                <h2 className={styles.panelTitle}>Portfolio by category</h2>
                <button type="button" className={styles.action} onClick={() => navigate({ to: '/regulatory/products' })}>Open register</button>
              </header>
              <ul className={styles.breakdown}>
                <li className={styles.breakRow}><span className={styles.breakLabel}>Pharmaceutical</span><span className={styles.breakBarWrap}><span className={styles.breakBar} style={{ width: `${Math.min(100, (p?.pharma ?? 0) / 3)}%` }} /></span><span className={styles.breakNum}>{p?.pharma ?? 0}</span></li>
                <li className={styles.breakRow}><span className={styles.breakLabel}>Nutraceutical</span><span className={styles.breakBarWrap}><span className={styles.breakBar} style={{ width: `${Math.min(100, (p?.nutra ?? 0) * 2)}%` }} /></span><span className={styles.breakNum}>{p?.nutra ?? 0}</span></li>
                <li className={styles.breakRow}><span className={styles.breakLabel}>Medical Device</span><span className={styles.breakBarWrap}><span className={styles.breakBar} style={{ width: `${Math.min(100, (p?.md ?? 0) * 1.5)}%` }} /></span><span className={styles.breakNum}>{p?.md ?? 0}</span></li>
              </ul>
            </section>
          </div>
        </>
      )}
    </main>
  );
}
